import { useRef, useState } from 'react';

import { submitLead } from 'api/lead';
import BrandSelect from './BrandSelect';
import { SiteLink } from './RedesignShell';

const createSubmissionId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

function LeadForm({
  formId = 'lead-form',
  idPrefix = 'f',
  eyebrow = '— форма заявки',
  submitLabel = 'Заказать digital-услуги →',
  sentLabel = 'отправлено · мы свяжемся ✓',
  contactLabel = 'Телефон',
  contactType = 'tel',
  contactPlaceholder = '+7 (___) ___-__-__',
  showTelegram = true,
  showCompany = true,
  typeOptions,
  typeLabel = 'Тип проекта',
  taskLabel = 'Кратко о задаче',
  consentText = 'Согласен с ',
  consentLinkLabel = 'политикой обработки персональных данных',
  taskPlaceholder = 'Например: нужен интернет-магазин и трафик с Яндекса',
  contextDescription = '',
  projectType = '',
}) {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submittingRef = useRef(false);
  const submissionIdRef = useRef(createSubmissionId());

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || sent) return;

    submittingRef.current = true;
    setSubmitting(true);
    setError('');

    const formData = new FormData(event.currentTarget);
    const route = typeof window === 'undefined' ? '/' : window.location.pathname;
    const payload = {
      submission_id: submissionIdRef.current,
      form_id: formId,
      route,
      name: formData.get('name'),
      phone_number: formData.get('contact'),
      contact_type: contactType,
      telegram: formData.get('telegram') || '',
      company: formData.get('company') || '',
      project_type: formData.get('project_type') || projectType,
      task_description: [contextDescription, formData.get('task_description') || ''].filter(Boolean).join('\n\n').slice(0, 5000),
      consent_received: formData.get('consent_received') === 'on',
      website: formData.get('website') || '',
    };

    try {
      await submitLead(payload);
      setSent(true);
    } catch (_error) {
      setError('Не удалось отправить заявку. Проверьте данные и попробуйте ещё раз.');
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <form className="lead-form" id={formId} onSubmit={handleSubmit}>
      <div className="eyebrow cyan" style={{ marginBottom: '24px' }}>
        {eyebrow}
      </div>
      <div className="field">
        <label htmlFor={`${idPrefix}-name`}>Имя</label>
        <input id={`${idPrefix}-name`} name="name" type="text" placeholder="Как к вам обращаться" minLength="2" maxLength="120" autoComplete="name" required />
      </div>
      <div className="field">
        <label htmlFor={`${idPrefix}-phone`}>{contactLabel}</label>
        <input id={`${idPrefix}-phone`} name="contact" type={contactType} placeholder={contactPlaceholder} maxLength="255" autoComplete={contactType === 'email' ? 'email' : 'tel'} required />
      </div>
      {showTelegram && (
        <div className="field">
          <label htmlFor={`${idPrefix}-telegram`}>Telegram</label>
          <input id={`${idPrefix}-telegram`} name="telegram" type="text" placeholder="Ваш ник в телеграм @" maxLength="100" autoComplete="off" />
        </div>
      )}
      {showCompany && (
        <div className="field">
          <label htmlFor={`${idPrefix}-company`}>Компания</label>
          <input id={`${idPrefix}-company`} name="company" type="text" placeholder="Название организации" maxLength="255" autoComplete="organization" />
        </div>
      )}
      {typeOptions?.length > 0 && (
        <div className="field">
          <label id={`${idPrefix}-type-label`} htmlFor={`${idPrefix}-type`}>{typeLabel}</label>
          <BrandSelect id={`${idPrefix}-type`} labelId={`${idPrefix}-type-label`} name="project_type" options={typeOptions.map(option => [option, option])} defaultValue={typeOptions[0]} />
        </div>
      )}
      <div className="field">
        <label htmlFor={`${idPrefix}-task`}>{taskLabel}</label>
        <textarea id={`${idPrefix}-task`} name="task_description" placeholder={taskPlaceholder} maxLength={contextDescription ? Math.max(0, 5000 - contextDescription.length - 2) : 5000} />
      </div>
      <div className="lead-form-honeypot" aria-hidden="true">
        <label htmlFor={`${idPrefix}-website`}>Сайт</label>
        <input id={`${idPrefix}-website`} name="website" type="text" tabIndex="-1" autoComplete="off" />
      </div>
      <label className="checkbox">
        <input name="consent_received" type="checkbox" required />
        <span>
          {consentText}<SiteLink href="/privacy/">{consentLinkLabel}</SiteLink>
        </span>
      </label>
      {error && <p className="lead-form-status lead-form-status-error" role="alert">{error}</p>}
      <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={sent || submitting} aria-live="polite">
        {sent ? sentLabel : (submitting ? 'отправляем…' : submitLabel)}
      </button>
    </form>
  );
}

export default LeadForm;
